import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X, Send, ArrowRight, MessageCircle, CheckCheck, MapPin } from "lucide-react";
import { toast } from "sonner";
import { RESORTS } from "@/data/resorts";

const API = process.env.REACT_APP_BACKEND_URL;

const GREETING =
    "Olá! ✨ Eu sou o consultor virtual da Destino Resorts. Posso ajudar você a encontrar o resort que mais combina com a viagem que está planejando. Para começar: qual é o motivo da sua viagem?";

const CHIPS = ["Férias em família", "Viagem a dois", "Natureza e descanso"];

const getSessionId = () => {
    let id = localStorage.getItem("dr_agent_session");
    if (!id) {
        id = window.crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem("dr_agent_session", id);
    }
    return id;
};

function RecCard({ rec, reason, onNavigate }) {
    const resort = RESORTS.find((r) => r.slug === rec.slug);
    return (
        <div className="overflow-hidden rounded-2xl border border-[#E5DEC9] bg-white shadow-sm" data-testid={`agent-rec-card-${rec.slug}`}>
            {resort && (
                <img src={resort.cardImage} alt={rec.name} loading="lazy" className="h-32 w-full object-cover" />
            )}
            <div className="p-4">
                <p className="font-serif text-base font-semibold text-forest-deep">{rec.name}</p>
                <p className="mt-0.5 flex items-center gap-1 text-[11px] text-stone-500">
                    <MapPin className="h-3 w-3 text-gold-hover" />
                    {rec.location}
                </p>
                {reason && <p className="mt-2 text-xs italic leading-relaxed text-stone-600">“{reason}”</p>}
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {(rec.highlights || []).slice(0, 3).map((h) => (
                        <span key={h} className="rounded-full bg-[#EAF4EE] px-2.5 py-1 text-[10px] font-semibold text-[#164B3A]">
                            {h}
                        </span>
                    ))}
                </div>
                <div className="mt-3.5 flex gap-2">
                    <button
                        type="button"
                        data-testid={`agent-rec-view-${rec.slug}`}
                        onClick={() => onNavigate(rec.url, null)}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border-2 border-forest-emerald px-3 py-2 text-[11px] font-semibold text-forest-emerald transition-colors hover:bg-forest-emerald hover:text-white"
                    >
                        Conhecer resort
                        <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                    <button
                        type="button"
                        data-testid={`agent-rec-quote-${rec.slug}`}
                        onClick={() => onNavigate(rec.url, "cotacao-resort")}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-wa px-3 py-2 text-[11px] font-semibold text-white transition-colors hover:bg-wa-dark"
                    >
                        <MessageCircle className="h-3.5 w-3.5" />
                        Solicitar cotação
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function AgentWidget() {
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const sessionId = useRef(getSessionId());
    const scrollRef = useRef(null);

    const isAdmin = location.pathname.startsWith("/admin");

    useEffect(() => {
        const handler = () => setOpen(true);
        window.addEventListener("open-agent", handler);
        return () => window.removeEventListener("open-agent", handler);
    }, []);

    useEffect(() => {
        if (!open || messages.length > 0) return;
        (async () => {
            try {
                const res = await fetch(`${API}/api/agent/session/${sessionId.current}`);
                const data = await res.json();
                if (data.messages?.length) {
                    setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
                    return;
                }
            } catch {
                /* sessão nova */
            }
            setMessages([{ role: "assistant", content: GREETING }]);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, open]);

    if (isAdmin) return null;

    const appendToken = (content) => {
        setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === "assistant" && last.streaming) {
                next[next.length - 1] = { ...last, content: last.content + content };
            }
            return next;
        });
    };

    const send = async (raw) => {
        const text = (raw ?? input).trim();
        if (!text || streaming) return;
        setInput("");
        setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "", streaming: true }]);
        setStreaming(true);
        try {
            const res = await fetch(`${API}/api/agent/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId.current, message: text }),
            });
            if (!res.ok || !res.body) throw new Error("Falha na conversa");
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const events = buffer.split("\n\n");
                buffer = events.pop();
                for (const ev of events) {
                    const line = ev.trim();
                    if (!line.startsWith("data:")) continue;
                    let data;
                    try {
                        data = JSON.parse(line.slice(5).trim());
                    } catch {
                        continue;
                    }
                    if (data.type === "token") appendToken(data.content);
                    if (data.type === "meta") {
                        setMessages((prev) => {
                            const next = [...prev];
                            const last = next[next.length - 1];
                            if (last?.role === "assistant") {
                                next[next.length - 1] = { ...last, streaming: false, recs: data.recommendations?.length ? data.recommendations : undefined, lead: data.lead || undefined };
                            }
                            return next;
                        });
                        if (data.lead?.created) {
                            toast.success("Solicitação de cotação registrada! Nossa equipe vai preparar tudo.");
                        }
                    }
                }
            }
        } catch {
            setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last?.role === "assistant") {
                    next[next.length - 1] = { ...last, streaming: false, content: last.content || "Tive uma instabilidade agora. Pode repetir, por gentileza?" };
                }
                return next;
            });
        } finally {
            setStreaming(false);
        }
    };

    const handleHuman = async () => {
        try {
            const res = await fetch(`${API}/api/agent/handoff`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId.current, message: "" }),
            });
            const data = await res.json();
            window.open(data.url, "_blank", "noopener,noreferrer");
        } catch {
            toast.error("Não consegui abrir o WhatsApp agora. Tente pelo botão verde da página.");
        }
    };

    const handleNavigate = (url, scrollTo) => {
        setOpen(false);
        navigate(url, scrollTo ? { state: { scrollTo } } : undefined);
    };

    const showChips = messages.length === 1 && messages[0].role === "assistant" && !streaming;

    return (
        <>
            <AnimatePresence>
                {!open && (
                    <motion.button
                        key="launcher"
                        type="button"
                        data-testid="agent-launcher"
                        onClick={() => setOpen(true)}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 16 }}
                        whileTap={{ scale: 0.95 }}
                        className="fixed bottom-4 left-4 z-50 inline-flex items-center gap-2.5 rounded-full border border-gold/40 bg-forest-deep px-5 py-3.5 text-xs font-semibold text-white shadow-2xl shadow-forest-deep/40 transition-all duration-200 hover:bg-forest-dark sm:bottom-7 sm:left-7 sm:text-sm"
                        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}
                    >
                        <Sparkles className="h-4 w-4 text-gold" />
                        Encontre seu resort ideal
                    </motion.button>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {open && (
                    <motion.section
                        key="panel"
                        data-testid="agent-panel"
                        role="dialog"
                        aria-label="Consultor virtual Destino Resorts"
                        initial={{ opacity: 0, y: 40, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 40, scale: 0.98 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed inset-0 z-[90] flex flex-col bg-sand sm:inset-auto sm:bottom-6 sm:right-6 sm:h-[min(74vh,680px)] sm:w-[420px] sm:rounded-3xl sm:border sm:border-[#E5DEC9] sm:shadow-2xl sm:shadow-forest-deep/30"
                    >
                        <header className="flex items-center gap-3 bg-forest-deep px-5 py-4 sm:rounded-t-3xl">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                                <Sparkles className="h-5 w-5" />
                            </span>
                            <div className="flex-1">
                                <p className="font-serif text-base font-semibold text-white">Consultor Virtual</p>
                                <p className="flex items-center gap-1.5 text-[11px] text-stone-300">
                                    <span className="h-1.5 w-1.5 rounded-full bg-wa" />
                                    Destino Resorts · online agora
                                </p>
                            </div>
                            <button
                                type="button"
                                data-testid="agent-human-btn"
                                onClick={handleHuman}
                                className="rounded-full bg-wa/15 px-3.5 py-2 text-[11px] font-semibold text-wa transition-colors hover:bg-wa hover:text-white"
                            >
                                Falar com um consultor
                            </button>
                            <button
                                type="button"
                                data-testid="agent-close-btn"
                                aria-label="Fechar consultor"
                                onClick={() => setOpen(false)}
                                className="flex h-9 w-9 items-center justify-center rounded-full text-stone-300 transition-colors hover:bg-white/10 hover:text-white"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </header>

                        <div ref={scrollRef} data-lenis-prevent data-testid="agent-messages" className="flex-1 space-y-4 overflow-y-auto px-4 py-5">
                            {messages.map((m, i) => (
                                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                                    <div className={`max-w-[88%] ${m.role === "user" ? "" : "w-full max-w-full"}`}>
                                        <div
                                            className={
                                                m.role === "user"
                                                    ? "rounded-2xl rounded-tr-sm bg-forest-emerald px-4 py-3 text-sm leading-relaxed text-white shadow-sm"
                                                    : "rounded-2xl rounded-tl-sm border border-[#EDE6D6] bg-white px-4 py-3 text-sm leading-relaxed text-forest-deep shadow-sm"
                                            }
                                        >
                                            {m.content}
                                            {m.streaming && !m.content && (
                                                <span className="inline-flex gap-1 py-1" aria-label="Digitando">
                                                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold" />
                                                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold" style={{ animationDelay: "0.15s" }} />
                                                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold" style={{ animationDelay: "0.3s" }} />
                                                </span>
                                            )}
                                        </div>
                                        {m.recs && (
                                            <div className="mt-3 space-y-3">
                                                {m.recs.map((rec) => (
                                                    <RecCard key={rec.slug} rec={rec} reason={rec.reason} onNavigate={handleNavigate} />
                                                ))}
                                            </div>
                                        )}
                                        {m.lead?.created && (
                                            <a
                                                href={m.lead.whatsapp_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid="agent-lead-whatsapp"
                                                className="mt-3 flex items-center justify-between gap-3 rounded-2xl bg-wa px-4 py-3.5 text-white shadow-lg shadow-emerald-900/20 transition-colors hover:bg-wa-dark"
                                            >
                                                <span>
                                                    <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-white/85">
                                                        <CheckCheck className="h-3.5 w-3.5" /> Solicitação registrada
                                                    </span>
                                                    <span className="mt-0.5 block text-sm font-semibold">Continuar com um consultor no WhatsApp</span>
                                                </span>
                                                <ArrowRight className="h-4 w-4 shrink-0" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {showChips && (
                                <div className="flex flex-wrap gap-2" data-testid="agent-quick-chips">
                                    {CHIPS.map((chip) => (
                                        <button
                                            key={chip}
                                            type="button"
                                            data-testid={`agent-chip-${chip}`}
                                            onClick={() => send(chip)}
                                            className="rounded-full border border-forest-emerald/30 bg-white px-4 py-2 text-xs font-semibold text-forest-emerald transition-colors hover:bg-forest-emerald hover:text-white"
                                        >
                                            {chip}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                send();
                            }}
                            className="flex items-center gap-2.5 border-t border-[#EDE6D6] bg-white px-4 py-3.5 sm:rounded-b-3xl"
                            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.875rem)" }}
                        >
                            <input
                                data-testid="agent-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Escreva sua mensagem..."
                                className="flex-1 rounded-full border border-[#E5DEC9] bg-sand px-4 py-3 text-sm text-forest-deep outline-none transition-colors placeholder:text-stone-400 focus:border-forest-emerald"
                                disabled={streaming}
                            />
                            <button
                                type="submit"
                                data-testid="agent-send-btn"
                                aria-label="Enviar mensagem"
                                disabled={streaming || !input.trim()}
                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-forest-emerald text-white transition-all hover:bg-forest-dark disabled:opacity-40 active:scale-90"
                            >
                                <Send className="h-4 w-4" />
                            </button>
                        </form>
                    </motion.section>
                )}
            </AnimatePresence>
        </>
    );
}
